import React from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeroSection from "../components/home/HeroSection";
import {
  BusinessUseCasesSection,
  CourseQuickSection,
  FeatureModulesSection,
  PricingSection,
} from "../components/home/HomeSalesSections";
import { useAuth } from "../context/AuthContext";

const WHATSAPP_URL = "https://wa.me/message/HOYXZTTFNXNZH1";

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const isAuthenticated = Boolean(user);
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();

  const continueAction = () => {
    if (loading) return;
    navigate(
      isAuthenticated
        ? "/dashboard"
        : referralCode
          ? `/registro?ref=${encodeURIComponent(referralCode)}`
          : "/registro",
    );
  };

  const primaryLabel = loading
    ? "Cargando..."
    : isAuthenticated
      ? "Abrir mi panel"
      : "Crear mi página gratis";

  const closingCopy = loading
    ? { eyebrow: "WebProfit", title: "Todo listo para seguir.", contactLabel: "Hablar con nosotros" }
    : isAuthenticated
      ? { eyebrow: "Sesión activa", title: "Seguimos desde tu panel.", contactLabel: "Necesito ayuda" }
      : { eyebrow: "Tu negocio puede empezar simple", title: "Publicalo. Empezá a trabajar. Sumá herramientas cuando hagan falta.", contactLabel: "Hablar con nosotros" };

  return (
    <Box component="main" sx={{ width: "100%", overflowX: "hidden", bgcolor: "#0B1016", color: "#F7F8FA" }}>
      <HeroSection
        onStartClick={continueAction}
        primaryLabel={primaryLabel}
        isAuthenticated={isAuthenticated}
        userName={user?.name || ""}
        onExploreClick={() => {
          document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />

      <BusinessUseCasesSection />
      <CourseQuickSection onOpenCourse={() => navigate("/documentacion")} />
      <FeatureModulesSection />
      <PricingSection onStartClick={continueAction} primaryLabel={primaryLabel} />

      <Box component="section" aria-labelledby="home-closing-title" sx={{ px: { xs: 2, md: 4 }, py: { xs: 6, sm: 8, md: 11 }, bgcolor: "#0B1016" }}>
        <Container maxWidth="lg" disableGutters>
          <Box sx={{ position: "relative", overflow: "hidden", borderRadius: { xs: 4, sm: 5, md: 7 }, px: { xs: 2.2, sm: 5, md: 8 }, py: { xs: 3.8, sm: 5.5, md: 7 }, color: "#fff", background: "radial-gradient(circle at 88% 12%, rgba(243,90,58,.32), transparent 28%), linear-gradient(135deg, #111820 0%, #17222D 62%, #1F2933 100%)", border: "1px solid rgba(255,255,255,.08)", boxShadow: "0 32px 80px rgba(0,0,0,.3)" }}>
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" spacing={{ xs: 2.8, md: 4 }} sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ maxWidth: 720 }}>
                <Typography sx={{ color: "#FFB49F", fontWeight: 900, letterSpacing: ".12em", textTransform: "uppercase", fontSize: ".78rem", mb: 1.25 }}>{closingCopy.eyebrow}</Typography>
                <Typography id="home-closing-title" component="h2" sx={{ fontWeight: 950, letterSpacing: "-.045em", lineHeight: 1.05, fontSize: { xs: "1.72rem", sm: "2.5rem", md: "3.15rem" } }}>{closingCopy.title}</Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.3} sx={{ width: { xs: "100%", md: "auto" } }}>
                <Button variant="contained" size="large" onClick={continueAction} disabled={loading} startIcon={isAuthenticated ? <DashboardRoundedIcon /> : undefined} endIcon={!isAuthenticated ? <ArrowForwardRoundedIcon /> : undefined} sx={{ minHeight: { xs: 50, sm: 54 }, px: { xs: 2.4, sm: 3 }, borderRadius: 999, bgcolor: "#F35A3A", fontWeight: 900, textTransform: "none", boxShadow: "0 14px 30px rgba(243,90,58,.3)", "&:hover": { bgcolor: "#DF4728" } }}>{primaryLabel}</Button>
                <Button variant="outlined" size="large" startIcon={<WhatsAppIcon />} onClick={() => (window.location.href = WHATSAPP_URL)} sx={{ minHeight: { xs: 48, sm: 54 }, px: { xs: 2.2, sm: 2.5 }, borderRadius: 999, color: "#fff", borderColor: "rgba(255,255,255,.26)", fontWeight: 850, textTransform: "none", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,.06)" } }}>{closingCopy.contactLabel}</Button>
              </Stack>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
